import { colors } from '@cabonline/ui-shared';
import { Text } from '@cabonline/ui-web';
import {
  NotificationCategorySummary,
  NotificationStatus,
  useLocale,
} from '@multiplatform-apps/service-trips';
import { forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { paths } from '../../../constants';
import {
  StyledPopover,
  StyledPopoverBadge,
  StyledPopoverRow,
  StyledViewAllButton,
} from './styles';

interface NotificationPopoverProps {
  categories: NotificationCategorySummary[];
  onClose: () => void;
  onViewAll?: () => void;
}

const STATUS_LABEL_KEYS: Record<NotificationStatus, string> = {
  NEW_PASSENGER: 'notifications.popover.incomingPassengers',
  AWAITING_APPROVAL: 'notifications.popover.updatedPassengers',
};

export const NotificationPopover = forwardRef<HTMLDivElement, NotificationPopoverProps>(
  ({ categories, onClose, onViewAll }, ref) => {
  const { t } = useLocale();
  const navigate = useNavigate();

  const handleViewAll = (): void => {
    onViewAll?.();
    onClose();
    navigate(paths.notifications.root);
  };

  const activeCategories = categories.filter((c) => c.count > 0);

  return (
    <StyledPopover ref={ref} role="dialog" aria-label={t('navBar.notifications')}>
      {activeCategories.length > 0 ? (
        activeCategories.map((category) => (
          <StyledPopoverRow key={category.status}>
            <Text weight="bold" size="m" color={colors.black}>
              {t(STATUS_LABEL_KEYS[category.status])}
            </Text>
            <StyledPopoverBadge aria-label={`${category.count}`}>
              {category.count}
            </StyledPopoverBadge>
          </StyledPopoverRow>
        ))
      ) : (
        <StyledPopoverRow>
          <Text size="m" color={colors.gray2}>
            {t('notifications.popover.noNew')}
          </Text>
        </StyledPopoverRow>
      )}
      <StyledViewAllButton onClick={handleViewAll}>
        {t('notifications.popover.viewAll')}
      </StyledViewAllButton>
    </StyledPopover>
  );
});

NotificationPopover.displayName = 'NotificationPopover';
